import { createAvatar } from '@dicebear/core';
import { notionists } from '@dicebear/collection';

const cache = new Map<string, string>();

/** Soft disc behind the face (hex without #). Hamssah espresso / parchment. */
const ROLE_PERSONA_BG: Record<string, string> = {
  SUPER_ADMIN: 'f5efe8', // menthol-50 — warm parchment
  CATALOG: 'f3ebe4', // sage-100
  INVENTORY: 'e8ddd3', // menthol-200
  SALES: 'f0ebe4', // feldspar-100
  ACCOUNTING: 'ebe5de', // espresso-100
  VIEWER: 'f7f3ef', // feldspar-50
  SHOWROOM_STAFF: 'fffefa', // cream
  STOREFRONT: 'f1e8e3', // blush-100
  WEB: 'f0ebe4', // feldspar-100
};

/** Line / fill ink — Hamssah burgundy + espresso (not WaPharma green). */
const ROLE_PERSONA_INK: Record<string, string> = {
  SUPER_ADMIN: '#3f0009', // pine-950 / burgundy
  CATALOG: '#3a2f2b', // espresso-800
  INVENTORY: '#664c46', // liver
  SALES: '#5c1a1a', // brunswick-brand-700
  ACCOUNTING: '#3a2f2b', // espresso-800
  VIEWER: '#6f5d56', // espresso-600 — softer read-only
  SHOWROOM_STAFF: '#564640', // espresso-700
  STOREFRONT: '#672020', // brunswick-brand-800
  WEB: '#5c1a1a', // brunswick-brand-700
};

const HAMSSAH_CREAM = '#fffefa';

const BRAND_BG_FALLBACK = ['fffefa', 'f3ebe4', 'f5efe8', 'ebe5de', 'f0ebe4'] as const;
const BRAND_INK_FALLBACK = ['#3a2f2b', '#564640', '#3f0009', '#664c46'] as const;

function hashRole(role: string): number {
  let h = 0;
  for (let i = 0; i < role.length; i += 1) h = (h + role.charCodeAt(i) * (i + 1)) % 997;
  return h;
}

function backgroundForRole(role: string): string {
  return ROLE_PERSONA_BG[role] ?? BRAND_BG_FALLBACK[hashRole(role) % BRAND_BG_FALLBACK.length]!;
}

function inkForRole(role: string): string {
  return ROLE_PERSONA_INK[role] ?? BRAND_INK_FALLBACK[hashRole(role) % BRAND_INK_FALLBACK.length]!;
}

/** Paint notionists (#000 / #fff) with Hamssah ink + cream body. */
function tintNotionistsSvg(svg: string, ink: string): string {
  return svg
    .replace(/#000000|#000\b/gi, ink)
    .replace(/#ffffff|#fff\b/gi, HAMSSAH_CREAM);
}

/** Deterministic Notion-style face, zoomed head, Hamssah brand colors. */
export function rolePersonaDataUri(role: string): string {
  const seed = role.trim() || 'role';
  const hit = cache.get(seed);
  if (hit) return hit;

  const raw = createAvatar(notionists, {
    seed,
    size: 96,
    scale: 195,
    translateY: 16,
    backgroundColor: [backgroundForRole(seed)],
  }).toString();

  const svg = tintNotionistsSvg(raw, inkForRole(seed));
  const uri = `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
  cache.set(seed, uri);
  return uri;
}
